<script setup>
import { computed, onMounted, ref } from 'vue';
import { useSiteForm } from '../useSiteForm.js';
import { styleVars } from './shared.js';
import EmailTypoHint from '@/Components/EmailTypoHint.vue';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const { values, errors, status, message, submit, turnstileKey, mountTurnstile } = useSiteForm('newsletter', {
    siteData: props.site_data,
    fields: { email: '' },
});

const turnstileEl = ref(null);
onMounted(() => { if (turnstileKey) mountTurnstile(turnstileEl.value); });

const successText = computed(() => props.config.success_message || message.value);
</script>

<template>
    <section :id="config.anchor_id || null" class="th-block th-newsletter th-tint" :style="styleVars(config)">
        <div class="th-wrap th-band--short th-newsletter__inner">
            <div class="th-newsletter__copy">
                <h2 v-if="config.heading" class="th-h2 th-newsletter__title">{{ config.heading }}</h2>
                <p v-if="config.body" class="th-body th-newsletter__body">{{ config.body }}</p>
            </div>
            <div class="th-newsletter__side">
                <div v-if="status === 'sent'" class="th-form-note th-form-note--ok th-newsletter__sent" role="status">{{ successText }}</div>
                <form v-else class="th-newsletter__form" novalidate @submit.prevent="submit()">
                    <div class="th-newsletter__row">
                        <input v-model="values.email" type="email" name="email" autocomplete="email"
                               class="th-input th-newsletter__email th-newsletter-email"
                               :placeholder="config.placeholder || 'Email address'"
                               :aria-label="config.placeholder || 'Email address'">
                        <input v-model="values.website" class="th-hp" type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true">
                        <button type="submit" class="th-btn th-btn--dark th-newsletter-submit" :disabled="status === 'sending'">
                            {{ status === 'sending' ? 'Sending…' : (config.button_label || 'Subscribe') }}
                        </button>
                    </div>
                    <EmailTypoHint :email="values.email" @fix="values.email = $event" />
                    <span v-if="errors.email" class="th-field-error">{{ errors.email }}</span>
                    <div v-if="turnstileKey" ref="turnstileEl" class="th-newsletter__turnstile"></div>
                    <div v-if="status === 'error' && message" class="th-form-note th-form-note--error" role="alert">{{ message }}</div>
                </form>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-newsletter__inner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
}
.th-newsletter__copy { max-width: 520px; }
.th-newsletter__title { font-size: clamp(26px, 3vw, 36px); line-height: 1.05; font-stretch: 106%; margin-bottom: 8px; }
.th-newsletter__body { font-size: 16px; color: var(--text-sub, #454a44); }
.th-newsletter__side { flex: 1; min-width: 260px; max-width: 480px; }
.th-newsletter__form { display: flex; flex-direction: column; gap: 8px; }
.th-newsletter__row { display: flex; flex-wrap: wrap; gap: 8px; }
.th-newsletter__email { flex: 1; min-width: 180px; width: auto; padding: 14px 16px; }
</style>
